import { Button } from "@/components/ui/button";
import { MonthPicker } from "@/components/ui/monthPicker";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useLocale } from "@/hooks/useLocale";
import { cn } from "@/lib/utils";
import { format } from "date-fns";

import { CalendarIcon } from "lucide-react";
import { forwardRef, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface ProjectLimitMonthRangeFilterProps {
  className?: string;
  popoverClassName?: string;
  value?: {
    from: string;
    to: string;
  };
  onMonthRangeChange?: (range: { from: string; to: string }) => void;
}

interface MonthRange {
  from: string;
  to: string;
}

const SelectedMonthLabel = ({ date }: { date: string }) => {
  const locale = useLocale();
  const monthName = format(date, "LLLL", { locale });
  const capitalizedMonth =
    monthName.charAt(0).toUpperCase() + monthName.slice(1);

  return <>{`${capitalizedMonth} ${new Date(date).getFullYear()}`}</>;
};

export default forwardRef<HTMLButtonElement, ProjectLimitMonthRangeFilterProps>(
  function ProjectLimitMonthRangeFilter(
    { className, popoverClassName, value, onMonthRangeChange, ...props },
    ref,
  ) {
    const { t } = useTranslation();
    const [selectedRange, setSelectedRange] = useState<MonthRange | undefined>(
      value,
    );

    useEffect(() => {
      if (value) {
        setSelectedRange(value);
      }
    }, [value]);

    const updateRange = useCallback(
      (newRange: MonthRange) => {
        setSelectedRange(newRange);
        onMonthRangeChange?.(newRange);
      },
      [onMonthRangeChange],
    );

    const handleFromMonthChange = useCallback(
      (date: Date) => {
        if (!selectedRange) {
          const newRange = {
            from: date.toISOString(),
            to: date.toISOString(),
          };
          updateRange(newRange);
          return;
        }

        const newRange = {
          from: date.toISOString(),
          to: selectedRange.to,
        };
        updateRange(newRange);
      },
      [selectedRange, updateRange],
    );

    const handleToMonthChange = useCallback(
      (date: Date) => {
        if (!selectedRange) {
          const newRange = {
            from: date.toISOString(),
            to: date.toISOString(),
          };
          updateRange(newRange);
          return;
        }

        const newRange = {
          from: selectedRange.from,
          to: date.toISOString(),
        };
        updateRange(newRange);
      },
      [selectedRange, updateRange],
    );

    return (
      <div className={cn("flex flex-row gap-2", className)}>
        <Popover>
          <PopoverTrigger asChild className={popoverClassName}>
            <Button
              variant="outline"
              className={cn(
                "justify-start text-left font-medium grow-0",
                !selectedRange && "text-muted-foreground",
              )}
              {...props}
              ref={ref}
            >
              <span className="text-muted-foreground">Od</span>
              <CalendarIcon className="mr-1 h-4 w-4" />
              {selectedRange ? (
                <SelectedMonthLabel date={selectedRange.from} />
              ) : (
                <span>{t("projectLimits.monthFilter.pickMonth")}</span>
              )}
              <span className="text-muted-foreground">do</span>
              <CalendarIcon className="mr-1 h-4 w-4" />
              {selectedRange ? (
                <SelectedMonthLabel date={selectedRange.to} />
              ) : (
                <span>{t("projectLimits.monthFilter.pickMonth")}</span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-2 flex flex-col gap-2">
            <div className="flex flex-row gap-2">
              <MonthPicker
                value={
                  selectedRange?.from ? new Date(selectedRange.from) : undefined
                }
                onChange={handleFromMonthChange}
              />
              <MonthPicker
                value={
                  selectedRange?.to ? new Date(selectedRange.to) : undefined
                }
                onChange={handleToMonthChange}
              />
            </div>
          </PopoverContent>
        </Popover>
      </div>
    );
  },
);
